import type { SkillNode, GraphNode } from './types';
import { zoneOrder } from './config';

export function buildGraph(skillData: SkillNode[]): {
	nodes: Record<string, GraphNode>;
	undirectedEdges: [string, string][];
} {
	const nodes: Record<string, GraphNode> = {};
	const undirectedEdges: [string, string][] = [];
	const seenPairs = new Set<string>();

	// Initialize nodes with base data
	skillData.forEach((node, index) => {
		nodes[node.id] = {
			...node,
			sourceIndex: index,
			neighbors: [],
			children: [],
			parents: [],
			treeChildren: [],
			level: -1
		};
	});

	// Build undirected edges from linked references
	skillData.forEach((node) => {
		(node.linked || []).forEach((targetId) => {
			if (!nodes[targetId] || targetId === node.id) return;

			if (!nodes[node.id].neighbors.includes(targetId)) {
				nodes[node.id].neighbors.push(targetId);
			}
			if (!nodes[targetId].neighbors.includes(node.id)) {
				nodes[targetId].neighbors.push(node.id);
			}

			const pairKey = [node.id, targetId].sort().join('|');
			if (!seenPairs.has(pairKey)) {
				seenPairs.add(pairKey);
				undirectedEdges.push([node.id, targetId]);
			}
		});
	});

	// Calculate levels using BFS starting from root node
	const queue: string[] = ['node1'];
	nodes['node1'].level = 0;

	while (queue.length > 0) {
		const currentId = queue.shift()!;
		const current = nodes[currentId];

		current.neighbors.forEach((neighborId) => {
			const neighbor = nodes[neighborId];
			const newLevel = current.level + 1;
			if (neighbor.level === -1 || newLevel < neighbor.level) {
				neighbor.level = newLevel;
				queue.push(neighborId);
			}
		});
	}

	// Build directed edges based on level ordering
	function addDirectedEdge(sourceId: string, targetId: string): void {
		if (!nodes[sourceId].children.includes(targetId)) {
			nodes[sourceId].children.push(targetId);
		}
		if (!nodes[targetId].parents.includes(sourceId)) {
			nodes[targetId].parents.push(sourceId);
		}
	}

	function addSameLevelEdge(leftId: string, rightId: string): void {
		if (!nodes[leftId].sameLevelNeighbors) nodes[leftId].sameLevelNeighbors = [];
		if (!nodes[rightId].sameLevelNeighbors) nodes[rightId].sameLevelNeighbors = [];
		if (!nodes[leftId].sameLevelNeighbors!.includes(rightId)) {
			nodes[leftId].sameLevelNeighbors!.push(rightId);
		}
		if (!nodes[rightId].sameLevelNeighbors!.includes(leftId)) {
			nodes[rightId].sameLevelNeighbors!.push(leftId);
		}
	}

	undirectedEdges.forEach(([leftId, rightId]) => {
		const left = nodes[leftId];
		const right = nodes[rightId];

		if (left.level < right.level) {
			addDirectedEdge(leftId, rightId);
			return;
		}

		if (right.level < left.level) {
			addDirectedEdge(rightId, leftId);
			return;
		}

		// Same-level links are stored separately for layouts that need them (e.g., wiki view)
		addSameLevelEdge(leftId, rightId);
	});

	// Assign zones based on type and inheritance
	assignZones(nodes);

	return { nodes, undirectedEdges };
}

function assignZones(nodes: Record<string, GraphNode>): void {
	// Set explicit type zones
	Object.values(nodes).forEach((node) => {
		if (node.type) node.zone = node.type;
	});
	nodes['node1'].zone = 'root';

	// Propagate zones from parents to children
	for (let pass = 0; pass < 20; pass++) {
		let changed = false;
		Object.values(nodes).forEach((node) => {
			if (node.zone) return;
			const parentZones = node.parents
				.map((parentId) => nodes[parentId]?.zone)
				.filter((zone): zone is string => !!zone && zone !== 'root');
			if (parentZones.length > 0) {
				const counts: Record<string, number> = {};
				parentZones.forEach((zone) => {
					counts[zone] = (counts[zone] || 0) + 1;
				});
				node.zone = Object.entries(counts).sort((a, b) => b[1] - a[1])[0][0];
				changed = true;
			}
		});
		if (!changed) break;
	}

	// If no parent zones, try child zones, otherwise default to general
	Object.values(nodes).forEach((node) => {
		if (node.zone) return;
		const childZones = node.children
			.map((childId) => nodes[childId]?.zone)
			.filter((zone): zone is string => !!zone && zone !== 'root');
		if (childZones.length > 0) {
			const counts: Record<string, number> = {};
			childZones.forEach((zone) => {
				counts[zone] = (counts[zone] || 0) + 1;
			});
			node.zone = Object.entries(counts).sort((a, b) => b[1] - a[1])[0][0];
		} else {
			node.zone = 'general';
		}
	});

	// Resolve placement zones for layout positioning
	resolvePlacementZones(nodes);
}

function resolvePlacementZones(nodes: Record<string, GraphNode>): void {
	nodes['node1'].placementZone = 'magic';

	for (let pass = 0; pass < 20; pass++) {
		let changed = false;
		Object.values(nodes).forEach((node) => {
			const nextPlacementZone = resolvePlacementZone(node, nodes);
			if (node.placementZone !== nextPlacementZone) {
				node.placementZone = nextPlacementZone;
				changed = true;
			}
		});
		if (!changed) break;
	}
}

function resolvePlacementZone(node: GraphNode, nodes: Record<string, GraphNode>): string {
	const zones = zoneOrder as readonly string[];
	if (zones.includes(node.zone!)) return node.zone!;

	const typedZone = zones.includes(node.type as string) ? node.type : null;
	if (typedZone) return typedZone;

	const parentZone = node.parents
		.map((parentId) => nodes[parentId])
		.map((parent) => parent?.placementZone || parent?.zone)
		.find((zone) => zones.includes(zone as string));
	if (parentZone) return parentZone;

	const childZone = node.children
		.map((childId) => nodes[childId])
		.map((child) => child?.placementZone || child?.zone)
		.find((zone) => zones.includes(zone as string));
	return childZone || 'magic';
}

export function choosePrimaryParent(node: GraphNode, nodes: Record<string, GraphNode>): string | null {
	const previousLevelParents = node.parents.filter((parentId) => nodes[parentId].level === node.level - 1);
	if (!previousLevelParents.length) return null;

	previousLevelParents.sort((leftId, rightId) => {
		const left = nodes[leftId];
		const right = nodes[rightId];
		const leftSameZone = left.zone === node.zone ? 1 : 0;
		const rightSameZone = right.zone === node.zone ? 1 : 0;
		const leftDistance = Math.abs((left.coord?.x ?? left.sourceIndex) - (node.coord?.x ?? node.sourceIndex));
		const rightDistance = Math.abs((right.coord?.x ?? right.sourceIndex) - (node.coord?.x ?? node.sourceIndex));
		const leftBranchChildren = left.children.filter(
			(childId) => nodes[childId].level === node.level && nodes[childId].zone === node.zone
		).length;
		const rightBranchChildren = right.children.filter(
			(childId) => nodes[childId].level === node.level && nodes[childId].zone === node.zone
		).length;
		return (
			rightSameZone - leftSameZone ||
			leftDistance - rightDistance ||
			leftBranchChildren - rightBranchChildren ||
			left.sourceIndex - right.sourceIndex
		);
	});
	return previousLevelParents[0];
}
